/**
 * Guests Store - Guest Attendance State Management
 *
 * Handles:
 * - Identifying guests (non-registered attendees) in a session
 * - Adding new guests (Person + Attendance creation)
 * - Removing guest attendances
 * - Offline support via IndexedDB + sync queue
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { guestsApi } from '@/api/guests.api'
import { db } from '@/db'
import { isNetworkError } from '@/api/client'
import { useRegistrationsStore } from '@/stores/registrations.store'
import { useAttendancesStore } from '@/stores/attendances.store'
import { useParticipantsStore } from '@/stores/participants.store'
import type { AttendanceDto } from '@/types/api.types'
import type { LocalGuest, SyncStatus } from '@/types/local.types'

/**
 * View-model for a guest attendance (server-synced)
 */
export interface GuestAttendance {
  personId: number
  attendanceId: number
  firstName: string
  lastName: string
  isPresent: boolean
}

export const useGuestsStore = defineStore('guests', () => {
  // ==========================================================================
  // State
  // ==========================================================================

  // Server guest attendances by session ID
  const guests = ref<Map<number, GuestAttendance[]>>(new Map())

  // Local (offline) guests by session ID
  const localGuests = ref<Map<number, LocalGuest[]>>(new Map())

  const isLoading = ref(false)
  const isSaving = ref(false)
  const error = ref<string | null>(null)

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get server-synced guests for a session
   */
  const getBySessionId = computed(() => {
    return (sessionId: number): GuestAttendance[] => {
      return guests.value.get(sessionId) || []
    }
  })

  /**
   * Get local (offline) guests for a session
   */
  const getLocalBySessionId = computed(() => {
    return (sessionId: number): LocalGuest[] => {
      return localGuests.value.get(sessionId) || []
    }
  })

  /**
   * Get total guest count for a session (server + local)
   */
  const getGuestCount = computed(() => {
    return (sessionId: number): number => {
      const serverCount = (guests.value.get(sessionId) || []).length
      const localCount = (localGuests.value.get(sessionId) || []).length
      return serverCount + localCount
    }
  })

  /**
   * Check if there are pending sync items for guests
   */
  const hasPendingSync = computed(() => {
    return (sessionId: number): boolean => {
      const local = localGuests.value.get(sessionId) || []
      return local.some((g) => g.syncStatus === 'pending')
    }
  })

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Identify guests from attendance data.
   * A guest is an attendee whose personId is NOT in the course registrations.
   */
  function identifyGuests(sessionId: number, courseId: number, attendances: AttendanceDto[]): void {
    const registrationsStore = useRegistrationsStore()
    const participantsStore = useParticipantsStore()
    const registeredIds = new Set(
      registrationsStore.getActiveByCourseId(courseId).map((r) => r.participantId)
    )

    const guestAttendances: GuestAttendance[] = attendances
      // Kein isPresent-Filter mehr: die Existenz des Attendance-Datensatzes IST die Anwesenheit
      // Ausgetretene Mitglieder sind keine Gäste — sie haben einen eigenen Abschnitt
      .filter(
        (a) =>
          !a.isFormerMember &&
          !registeredIds.has(a.personId) &&
          !participantsStore.getById(a.personId)
      )
      .map((a) => {
        // Parse participantName "FirstName LastName"
        const nameParts = (a.participantName || '').split(' ')
        const firstName = nameParts[0] || ''
        const lastName = nameParts.slice(1).join(' ') || ''

        return {
          personId: a.personId,
          attendanceId: a.id,
          firstName,
          lastName,
          // Der Datensatz existiert, also ist der Gast anwesend
          isPresent: true,
        }
      })

    guests.value.set(sessionId, guestAttendances)
  }

  /**
   * Fetch and identify guests for a session.
   * Uses the attendance data already loaded by the attendances store.
   */
  async function fetchBySession(
    sessionId: number,
    courseId: number,
    forceRefresh = false
  ): Promise<void> {
    // Return cached if available and not forcing refresh
    if (!forceRefresh && guests.value.has(sessionId)) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const attendancesStore = useAttendancesStore()
      const attendances = attendancesStore.getBySessionId(sessionId)
      identifyGuests(sessionId, courseId, attendances)

      // Also load any local pending guests
      await loadLocalGuests(sessionId)
    } catch (e) {
      if (isNetworkError(e)) {
        await loadLocalGuests(sessionId)
        if (!guests.value.has(sessionId)) {
          error.value = 'Offline - Keine gecachten Gäste-Daten verfügbar'
        }
      } else {
        error.value = e instanceof Error ? e.message : 'Fehler beim Laden der Gäste'
        throw e
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Add a guest to a session.
   * Online: Creates Person + Attendance via API.
   * Offline: Saves to IndexedDB + sync queue.
   */
  async function addGuest(
    sessionId: number,
    courseId: number,
    firstName: string,
    lastName: string,
    email?: string,
    phone?: string
  ): Promise<void> {
    isSaving.value = true
    error.value = null

    try {
      if (navigator.onLine) {
        // Online: Create via API
        const { person, attendance } = await guestsApi.createWithAttendance(
          firstName,
          lastName,
          sessionId,
          email,
          phone
        )

        // Add to server state
        const sessionGuests = guests.value.get(sessionId) || []
        sessionGuests.push({
          personId: person.id,
          attendanceId: attendance.id,
          firstName: person.firstName,
          lastName: person.lastName,
          isPresent: true,
        })
        guests.value.set(sessionId, [...sessionGuests])

        // Also add to the attendances store so counts stay consistent
        const attendancesStore = useAttendancesStore()
        const serverAttendances = attendancesStore.getBySessionId(sessionId)
        attendancesStore.attendances.set(sessionId, [...serverAttendances, attendance])
      } else {
        // Offline: Store locally
        await saveLocalGuest(sessionId, courseId, firstName, lastName, email, phone)
      }
    } catch (e) {
      if (isNetworkError(e)) {
        // Network error: Save locally
        await saveLocalGuest(sessionId, courseId, firstName, lastName, email, phone)
      } else {
        error.value = e instanceof Error ? e.message : 'Fehler beim Hinzufügen des Gastes'
        throw e
      }
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Remove a guest from a session.
   * Online: Deletes attendance via API.
   * Offline: Adds to sync queue.
   */
  async function removeGuest(
    sessionId: number,
    personId: number,
    attendanceId?: number,
    localId?: string
  ): Promise<void> {
    isSaving.value = true
    error.value = null

    try {
      if (localId) {
        // Remove local guest
        await removeLocalGuest(sessionId, localId)
        // Also remove from sync queue
        await db.syncQueue.where('localId').equals(localId).delete()
      } else if (attendanceId) {
        // Remove server guest
        if (navigator.onLine) {
          await guestsApi.removeAttendance(attendanceId)
        }

        // Remove from server state
        const sessionGuests = guests.value.get(sessionId) || []
        guests.value.set(
          sessionId,
          sessionGuests.filter((g) => g.personId !== personId)
        )

        // Also remove from attendances store
        const attendancesStore = useAttendancesStore()
        const serverAttendances = attendancesStore.getBySessionId(sessionId)
        attendancesStore.attendances.set(
          sessionId,
          serverAttendances.filter((a) => a.id !== attendanceId)
        )
      }
    } catch (e) {
      if (isNetworkError(e)) {
        error.value = 'Offline - Gast kann nur online entfernt werden'
      } else {
        error.value = e instanceof Error ? e.message : 'Fehler beim Entfernen des Gastes'
        throw e
      }
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Save a guest locally (for offline)
   */
  async function saveLocalGuest(
    sessionId: number,
    courseId: number,
    firstName: string,
    lastName: string,
    email?: string,
    phone?: string
  ): Promise<void> {
    const localGuest: LocalGuest = {
      localId: uuidv4(),
      courseId,
      sessionId,
      firstName,
      lastName,
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      syncStatus: 'pending' as SyncStatus,
      createdAt: new Date().toISOString(),
    }

    // Save to IndexedDB
    await db.guests.put(localGuest)

    // Add to sync queue
    await db.syncQueue.put({
      type: 'guest',
      action: 'create',
      localId: localGuest.localId,
      data: localGuest,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    })

    // Update local state
    const local = localGuests.value.get(sessionId) || []
    local.push(localGuest)
    localGuests.value.set(sessionId, [...local])
  }

  /**
   * Remove a local guest
   */
  async function removeLocalGuest(sessionId: number, localId: string): Promise<void> {
    await db.guests.where('localId').equals(localId).delete()

    const local = localGuests.value.get(sessionId) || []
    localGuests.value.set(
      sessionId,
      local.filter((g) => g.localId !== localId)
    )
  }

  /**
   * Load local guests from IndexedDB
   */
  async function loadLocalGuests(sessionId: number): Promise<void> {
    try {
      const local = await db.guests.where('sessionId').equals(sessionId).toArray()
      localGuests.value.set(sessionId, local)
    } catch (e) {
      console.warn('[GuestsStore] Failed to load local guests:', e)
    }
  }

  /**
   * Sync pending guests to server
   */
  async function syncPending(): Promise<{ success: number; failed: number }> {
    const pending = await db.syncQueue.where('type').equals('guest').toArray()

    let success = 0
    let failed = 0

    for (const item of pending) {
      try {
        const localData = item.data as LocalGuest

        if (item.action === 'create') {
          const { person, attendance } = await guestsApi.createWithAttendance(
            localData.firstName,
            localData.lastName,
            localData.sessionId,
            localData.email,
            localData.phone
          )

          // Update server state
          const sessionGuests = guests.value.get(localData.sessionId) || []
          sessionGuests.push({
            personId: person.id,
            attendanceId: attendance.id,
            firstName: person.firstName,
            lastName: person.lastName,
            isPresent: true,
          })
          guests.value.set(localData.sessionId, [...sessionGuests])

          // Also update attendances store
          const attendancesStore = useAttendancesStore()
          const serverAttendances = attendancesStore.getBySessionId(localData.sessionId)
          attendancesStore.attendances.set(localData.sessionId, [...serverAttendances, attendance])
        }

        // Remove from sync queue and local storage
        await db.syncQueue.delete(item.id!)
        await db.guests.where('localId').equals(localData.localId).delete()

        // Remove from local state
        const local = localGuests.value.get(localData.sessionId) || []
        localGuests.value.set(
          localData.sessionId,
          local.filter((g) => g.localId !== localData.localId)
        )

        success++
      } catch (e) {
        // Update retry count
        await db.syncQueue.update(item.id!, {
          retryCount: item.retryCount + 1,
          lastError: e instanceof Error ? e.message : 'Unknown error',
        })
        failed++
      }
    }

    return { success, failed }
  }

  /**
   * Clear session data
   */
  function clearSession(sessionId: number): void {
    guests.value.delete(sessionId)
    localGuests.value.delete(sessionId)
  }

  /**
   * Reset store
   */
  function $reset(): void {
    guests.value.clear()
    localGuests.value.clear()
    isLoading.value = false
    isSaving.value = false
    error.value = null
  }

  return {
    // State
    guests,
    localGuests,
    isLoading,
    isSaving,
    error,

    // Getters
    getBySessionId,
    getLocalBySessionId,
    getGuestCount,
    hasPendingSync,

    // Actions
    fetchBySession,
    identifyGuests,
    addGuest,
    removeGuest,
    syncPending,
    clearSession,
    $reset,
  }
})
